import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { Box, Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';

import useImageUrlGenerator from '~/hooks/useImageUrlGenerator';

import { ALLOWED_IMAGE_MIMETYPES, getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';

interface Props {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  error?: boolean;
  /** Whether the image can be removed; off where one is required. */
  removable?: boolean;
}

const PREVIEW = { width: 240, height: 160 };
const ENLARGED = { width: 1200, height: 800 };

/** One uploaded image: a preview, and buttons to upload, replace or remove it. */
export default function NativeTourImageField({ label, value, onChange, error = false, removable = true }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const previewUrl = useImageUrlGenerator(value ?? '', PREVIEW);
  const enlargedUrl = useImageUrlGenerator(value ?? '', ENLARGED);
  const [isEnlarged, setIsEnlarged] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setIsUploading(true);
    try {
      const id = getUploadedImageId(await uploadImage(file));
      if (!id) {
        throw new Error('The image service did not return an image id');
      }
      onChange(id);
    } catch (uploadError) {
      enqueueSnackbar(getErrorMessage(uploadError, 'Failed to upload the image'), { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
        {label}
      </Typography>
      <Box
        role={value ? 'button' : undefined}
        aria-label={value ? `Enlarge ${label}` : undefined}
        onClick={() => value && setIsEnlarged(true)}
        sx={{
          cursor: value ? 'zoom-in' : 'default',
          width: PREVIEW.width,
          height: PREVIEW.height,
          borderRadius: 1,
          border: 1,
          borderStyle: value ? 'solid' : 'dashed',
          borderColor: error ? 'error.main' : 'divider',
          bgcolor: 'action.hover',
          backgroundImage: value ? `url(${previewUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isUploading && <CircularProgress size={24} />}
        {!isUploading && !value && <AddPhotoAlternateOutlinedIcon color="disabled" />}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button component="label" size="small" disabled={isUploading}>
          {value ? 'Replace' : 'Upload'}
          <input
            hidden
            type="file"
            accept={ALLOWED_IMAGE_MIMETYPES.join(',')}
            onChange={(event) => {
              handleFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </Button>
        {value && (
          <Dialog open={isEnlarged} onClose={() => setIsEnlarged(false)} maxWidth="lg">
            <DialogContent sx={{ p: 0 }}>
              <Box
                component="img"
                src={enlargedUrl}
                alt={label}
                onClick={() => setIsEnlarged(false)}
                sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh', cursor: 'zoom-out' }}
              />
            </DialogContent>
          </Dialog>
        )}
        {removable && value && (
          <Button size="small" variant="text" onClick={() => onChange(null)} disabled={isUploading}>
            Remove
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
